import { useMemo, useState } from 'react';

import { partRows } from '../../../lib/model-doc.js';
import { useModel } from '../../../state/model.js';
import { BAKE_MAPS, usePaint, type BakeMapName, type BakeOptions } from '../../../state/paint.js';
import { Card, NumberField, PropRow, SelectField, Toggle } from '../../ui/controls.js';
import './paint.css';

const SIZES = [256, 512, 1024, 2048, 4096].map((s) => ({ value: String(s), label: `${s} × ${s}` }));

/** Remembered settings per low part for this session. */
const remembered = new Map<string, Omit<BakeOptions, 'low'>>();

const DEFAULTS: Omit<BakeOptions, 'low'> = {
  high: [],
  maps: ['normal', 'ao'],
  size: 1024,
  extrusion: 0.05,
  maxDistance: 0,
  cage: null,
  aoSamples: 16,
  margin: 16,
  aoOtherParts: true,
  aoIncludeLow: false,
};

/**
 * High → low bake settings for the part `low`: high-poly sources, maps,
 * size, ray extrusion / distance or a cage; results are written to
 * `textures/<model>/` and wired into the part's material (one undo step).
 */
export function BakePanel({ low }: { low: string }) {
  const doc = useModel((s) => s.doc);
  const bake = usePaint((s) => s.bake);
  const [o, setO] = useState<Omit<BakeOptions, 'low'>>(() => remembered.get(low) ?? DEFAULTS);
  const parts = useMemo(() => (doc ? partRows(doc).filter((r) => r.partPath !== low && r.part.shape) : []), [doc, low]);
  const update = (patch: Partial<Omit<BakeOptions, 'low'>>) => {
    const next = { ...o, ...patch };
    remembered.set(low, next);
    setO(next);
  };
  const toggleHigh = (path: string, on: boolean) => update({ high: on ? [...o.high, path] : o.high.filter((p) => p !== path) });
  const toggleMap = (map: BakeMapName, on: boolean) => update({ maps: on ? [...o.maps, map] : o.maps.filter((m) => m !== map) });
  const high = o.high.filter((h) => parts.some((p) => p.partPath === h));
  const ready = high.length > 0 && o.maps.length > 0 && !bake.running;
  return (
    <div className="bake-panel">
      <div className="paint-sep">High-poly sources</div>
      {parts.length === 0 ? (
        <p className="muted small">Add the detailed parts to this model first (e.g. a sculpt over the same shape).</p>
      ) : (
        <ul className="bake-parts">
          {parts.map((r) => (
            <li key={r.partPath} style={{ paddingLeft: r.depth * 10 }}>
              <Toggle checked={high.includes(r.partPath)} label={r.partPath} onChange={(v) => toggleHigh(r.partPath, v)} />
            </li>
          ))}
        </ul>
      )}
      <div className="paint-sep">Maps</div>
      <div className="bake-maps">
        {BAKE_MAPS.map((m) => (
          <span key={m.map} title={m.title}>
            <Toggle checked={o.maps.includes(m.map)} label={m.label} onChange={(v) => toggleMap(m.map, v)} />
          </span>
        ))}
      </div>
      <PropRow label="Size">
        <SelectField value={String(o.size)} options={SIZES} onChange={(v) => update({ size: Number(v) })} />
      </PropRow>
      <PropRow label="Extrusion" hint="Rays start this far outside the low surface (meters): larger than the high mesh's bulges">
        <NumberField value={o.extrusion} step={0.005} min={0} onChange={(v) => update({ extrusion: v })} />
      </PropRow>
      <PropRow label="Max distance" hint="How far inside the low surface rays search (0 = no limit)">
        <NumberField value={o.maxDistance} step={0.01} min={0} onChange={(v) => update({ maxDistance: v })} />
      </PropRow>
      <PropRow label="Cage" hint="Optional part with the low part's vertices (pushed out) where rays start">
        <SelectField
          value={o.cage ?? ''}
          options={[{ value: '', label: 'None (extrude)' }, ...parts.map((r) => ({ value: r.partPath, label: r.partPath }))]}
          onChange={(v) => update({ cage: v || null })}
        />
      </PropRow>
      {o.maps.includes('ao') && (
        <>
          <PropRow label="AO samples">
            <NumberField value={o.aoSamples} step={1} min={1} max={256} integer onChange={(v) => update({ aoSamples: v })} />
          </PropRow>
          <PropRow label="Other parts occlude" hint="The model's other visible parts shade the AO">
            <Toggle checked={o.aoOtherParts} onChange={(v) => update({ aoOtherParts: v })} />
          </PropRow>
          <PropRow label="Low-poly occludes" hint="The low-poly mesh itself also blocks AO rays">
            <Toggle checked={o.aoIncludeLow} onChange={(v) => update({ aoIncludeLow: v })} />
          </PropRow>
        </>
      )}
      <PropRow label="Margin (px)" hint="Gutter padding around UV islands">
        <NumberField value={o.margin} step={1} min={0} max={64} integer onChange={(v) => update({ margin: v })} />
      </PropRow>
      {bake.running && (
        <div className="bake-progress" role="progressbar" aria-valuenow={Math.round(bake.progress * 100)} aria-valuemin={0} aria-valuemax={100}>
          <span style={{ width: `${Math.round(bake.progress * 100)}%` }} />
        </div>
      )}
      {bake.warning && <p className="paint-layers-warning">{bake.warning}</p>}
      {bake.error && <p className="error small">{bake.error}</p>}
      {bake.result && !bake.running && <p className="muted small">{bake.result}</p>}
      <div className="paint-actions">
        {bake.running ? (
          <button type="button" className="ghost small" onClick={() => usePaint.getState().cancelBake()}>
            Cancel ({Math.round(bake.progress * 100)}%)
          </button>
        ) : (
          <button type="button" className="primary small" disabled={!ready} onClick={() => usePaint.getState().startBake({ low, ...o, high })}>
            Bake
          </button>
        )}
      </div>
    </div>
  );
}

/** "Bake maps" card for the part inspector. */
export function BakeCard({ path }: { path: string | null }) {
  if (!path) return null;
  return (
    <Card title="Bake maps" icon="image" defaultOpen={false} subtitle="high → low">
      <BakePanel low={path} />
    </Card>
  );
}
